import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Title from 'components/Title';
import { MultiLanguageRichTextField, SelectField } from 'components/inputs';
import { FormContainer, FormItem, FormRow } from 'components/styledComponents';
import { useLanguagesPaths, useTranslation } from 'hooks';

function Responsibilities({
  className,
  getValue,
  language,
  meta = {}, // ✅ Default empty object
  useInput
}) {
  const { tr } = useTranslation();

  // Destructure with default values
  const {
    companySituations = [],
    anualRevenues = [],
    budgets = [],
    coreCompetencies = [],
    coreCompetenciesByFunction: rawCoreCompetenciesByFunction = {},
    directReports = [],
    employees = []
  } = meta;

  // Normalize coreCompetenciesByFunction to always be an object
  const coreCompetenciesByFunction = Array.isArray(rawCoreCompetenciesByFunction)
    ? {}
    : rawCoreCompetenciesByFunction;

  const functionId = getValue('function');
  const functionIdRef = useRef(functionId);

  // const filteredCompetencies = useMemo(
  //   () =>
  //     coreCompetencies.filter(
  //       competency =>
  //         coreCompetenciesByFunction[functionId] &&
  //         coreCompetenciesByFunction[functionId].includes(competency.key)
  //     ),
  //   [coreCompetencies, coreCompetenciesByFunction, functionId]
  // );

  const filteredCompetencies = useMemo(() => {
    const keys = coreCompetenciesByFunction?.[functionId] ?? [];
    return coreCompetencies.filter(c => keys.includes(c.key));
  }, [coreCompetencies, coreCompetenciesByFunction, functionId]);

  const { onChange } = useInput('coreCompetencies');

  useEffect(() => {
    if (functionIdRef.current !== functionId) {
      functionIdRef.current = functionId;
      onChange([]);
    }
  }, [functionId, onChange]);

  return (
    <FormContainer className={className}>
      <FormRow>
        <FormItem component={Title} label={tr('title.responsibilitiesAndTasks')} size="large" />
      </FormRow>

      <FormRow>
        <FormItem
          component={SelectField}
          label={tr('label.revenueResponsibility')}
          options={anualRevenues}
          {...useInput('revenueResponsability')}
        />
        <FormItem
          component={SelectField}
          label={tr('label.budgetResponsibility')}
          options={budgets}
          {...useInput('costBudgetResponsability')}
        />
      </FormRow>

      <FormRow>
        <FormItem
          component={SelectField}
          label={tr('label.personnelResponsibility')}
          options={employees}
          {...useInput('personnelResponsability')}
        />
        <FormItem
          component={SelectField}
          label={tr('label.numberOfReports')}
          options={directReports}
          {...useInput('noOfDirectReports')}
        />
      </FormRow>

      <FormRow>
        <FormItem
          component={SelectField}
          label={tr('label.companySituation')}
          options={companySituations}
          {...useInput('companySituation')}
        />
        <FormItem />
      </FormRow>

      <FormRow>
        <FormItem component="p">{tr('message.threeCoreCompetencies')}</FormItem>
      </FormRow>

      <FormRow>
        <FormItem
          component={SelectField}
          isMulti
          label={tr('label.coreCompetencies')}
          maxSelections={3}
          options={filteredCompetencies}
          {...useInput('coreCompetencies')}
        />
      </FormRow>

      <FormRow>
        <FormItem component="p">{tr('message.describeResponsibilities')}</FormItem>
      </FormRow>

      <FormRow>
        <FormItem
          component={MultiLanguageRichTextField}
          data-rttextarea-id="description-rich-editor"
          language={language}
          {...useInput('description', { alternateFields: useLanguagesPaths('description') })}
        />
      </FormRow>
    </FormContainer>
  );
}

Responsibilities.propTypes = {
  className: PropTypes.string,
  getValue: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    companySituations: PropTypes.array,
    anualRevenues: PropTypes.array,
    budgets: PropTypes.array,
    coreCompetencies: PropTypes.array,
    coreCompetenciesByFunction: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array // allow array temporarily to suppress warning
    ]),
    directReports: PropTypes.array,
    employees: PropTypes.array
  }),
  useInput: PropTypes.func.isRequired
};

export default Responsibilities;
